import { useEffect, useRef, useState } from "react";
import type {
  WellbeingSeries,
  WellbeingSeriesView,
} from "@care-by-ella/contracts";
import {
  fetchPatientWellbeingSeries,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { formatSymptomInstant } from "@/lib/symptom-lines";
import {
  drawsLine,
  wellbeingChart,
  wellbeingChartSummary,
  wellbeingProvenance,
} from "@/lib/wellbeing-lines";

// TES-1446 (Ö-654, Ö-675) — "Mående" on the patient overview: her answers
// to the wellbeing questions, the series her own "Din hälsa över tid"
// shows, from the same server read. F-05's wellbeing answer is that week's
// one point on the F-04 line, so the doctor never sees a week twice.
//
// Doctor only, read only: the route answers 403 to anyone else.
//
// Inline SVG, like the other clinical curves in the admin: a line, dots and
// the scale's words, with the full answer list as the record beside it.

type State =
  | { state: "loading" }
  | { state: "error" }
  | { state: "ok"; view: WellbeingSeriesView };

const VIEW_W = 640;
const VIEW_H = 140;
const PAD_LEFT = 104;
const PAD_RIGHT = 16;
const PAD_TOP = 12;
const PAD_BOTTOM = 12;
const INNER_W = VIEW_W - PAD_LEFT - PAD_RIGHT;
const INNER_H = VIEW_H - PAD_TOP - PAD_BOTTOM;

function xAt(index: number, count: number): number {
  // Index spacing: one answer per check-in, evenly spaced; the dates are in
  // the list below.
  if (count <= 1) return PAD_LEFT + INNER_W / 2;
  return PAD_LEFT + (index / (count - 1)) * INNER_W;
}

const yAt = (y: number) => PAD_TOP + y * INNER_H;

function Chart({ series }: { series: WellbeingSeries }) {
  const chart = wellbeingChart(series);
  const count = chart.points.length;
  const levelStep = chart.levels.length > 1 ? 1 / (chart.levels.length - 1) : 0;
  const line = chart.points
    .map((p, i) => ({ p, i }))
    .filter(({ p }) => p.kind === "line")
    .map(({ p, i }) => `${xAt(i, count)},${yAt(p.y)}`)
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      className="w-full max-w-2xl"
      role="img"
      aria-label={wellbeingChartSummary(series)}
    >
      {chart.levels.map((l, i) => {
        const y = yAt(chart.levels.length > 1 ? i * levelStep : 0.5);
        return (
          <g key={l.value}>
            <line
              x1={PAD_LEFT}
              x2={VIEW_W - PAD_RIGHT}
              y1={y}
              y2={y}
              className="text-surfaceMuted"
              stroke="currentColor"
              strokeWidth={1}
            />
            <text
              x={PAD_LEFT - 8}
              y={y}
              textAnchor="end"
              dominantBaseline="middle"
              className="text-textSecondary"
              fill="currentColor"
              fontSize={11}
            >
              {l.label}
            </text>
          </g>
        );
      })}
      <polyline
        points={line}
        fill="none"
        className="text-brand"
        stroke="currentColor"
        strokeWidth={2}
      />
      {chart.points.map((p, i) => {
        const x = xAt(i, count);
        const y = yAt(p.y);
        return p.kind === "line" ? (
          <circle
            key={p.point.id}
            cx={x}
            cy={y}
            r={4}
            className="text-brand"
            fill="currentColor"
          >
            <title>{`${p.point.label.sv}, ${formatSymptomInstant(p.point.observedAt)}`}</title>
          </circle>
        ) : (
          <rect
            key={p.point.id}
            x={x - 4}
            y={y - 4}
            width={8}
            height={8}
            transform={`rotate(45 ${x} ${y})`}
            className="text-textSecondary"
            fill="currentColor"
          >
            <title>{`${p.point.label.sv}, ${formatSymptomInstant(p.point.observedAt)}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

function Series({ series }: { series: WellbeingSeries }) {
  const latest = series.points[series.points.length - 1]!;
  const newestFirst = [...series.points].reverse();
  return (
    <li className="flex flex-col gap-xs border-t border-surfaceMuted pt-sm first:border-t-0 first:pt-0">
      <span className="text-textSecondary">{series.question.sv}</span>
      <span className="font-medium">{latest.label.sv}</span>
      <span className="text-xs text-textSecondary">
        {wellbeingProvenance(latest)}
      </span>
      {drawsLine(series) ? <Chart series={series} /> : null}
      <details>
        <summary className="cursor-pointer text-xs">
          Alla svar ({series.points.length})
        </summary>
        <ol className="mt-xs flex flex-col gap-xs pl-sm">
          {newestFirst.map((p) => (
            <li key={p.id} className="flex flex-col">
              <span>{p.label.sv}</span>
              <span className="text-xs text-textSecondary">
                {wellbeingProvenance(p)}
              </span>
            </li>
          ))}
        </ol>
      </details>
    </li>
  );
}

export function WellbeingSeriesPanel({
  patientId,
  onUnauthorized,
}: {
  patientId: string;
  onUnauthorized: () => void;
}) {
  const [result, setResult] = useState<State>({ state: "loading" });
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  useEffect(() => {
    let live = true;
    setResult({ state: "loading" });
    fetchPatientWellbeingSeries(patientId)
      .then((view) => {
        if (live) setResult({ state: "ok", view });
      })
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof UnauthorizedError) {
          onUnauthorizedRef.current();
          return;
        }
        setResult({ state: "error" });
      });
    return () => {
      live = false;
    };
  }, [patientId]);

  if (result.state === "loading") {
    return <p className="text-xs text-textSecondary">Läser mående …</p>;
  }
  if (result.state === "error") {
    return (
      <p className="text-xs text-textSecondary">
        Måendet kunde inte läsas. Ladda om sidan.
      </p>
    );
  }
  return <WellbeingSeriesList view={result.view} />;
}

/**
 * The series of one view, drawn. TES-1482 — the coach's graphs draw her
 * granted series with it, so the two views cannot differ in how an answer
 * is drawn.
 */
export function WellbeingSeriesList({
  view,
  empty = "Inga svar om mående än.",
}: {
  view: WellbeingSeriesView;
  empty?: string;
}) {
  const answered = view.series.filter((s) => s.points.length > 0);
  if (answered.length === 0) {
    return <p className="text-sm text-textSecondary">{empty}</p>;
  }
  return (
    <ul className="flex flex-col gap-sm text-sm">
      {answered.map((s) => (
        <Series key={s.questionId} series={s} />
      ))}
    </ul>
  );
}
